/**
 * "Create a Credit Note / Sales Return" — the step between an invoice and its editor.
 *
 * IT IS A HOP, NOT A SCREEN
 * ------------------------
 * There is no form here. A return always begins from one specific invoice, and the
 * invoice screen is where the user already is; the only decision this route
 * carries is WHICH of the two documents to create. So it creates the draft and
 * immediately navigates to the editor, and the user never sees an intermediate
 * page asking for something they have already chosen.
 *
 * The draft is created with EVERYTHING still returnable, which is the common case
 * (a customer hands back what they bought). The editor is where a partial return
 * is expressed, by adjusting quantities — so nobody is asked twice.
 *
 * The creation runs in a `useEffect` rather than during render because it is a
 * WRITE with a navigation after it. Doing it during render would fire twice under
 * React's StrictMode double-invocation and create two drafts.
 */
import { useEffect, useRef, useState } from 'react';
import { returnTypeLabel, type ReturnDocType } from '@data/services/salesReturnService';
import { toast } from '@ui/components/Toaster';
import { useCreateReturn } from './useSalesReturns';

const BASE: Record<ReturnDocType, string> = {
  CREDIT_NOTE: '#/sales/credit-notes',
  SALES_RETURN: '#/sales/returns',
};

export function NewReturnScreen({
  docType,
  invoiceId,
}: {
  docType: ReturnDocType;
  invoiceId: string;
}) {
  const create = useCreateReturn(docType);
  const label = returnTypeLabel(docType);
  const [error, setError] = useState<string | null>(null);
  // The guard, not the mutation's own state: React StrictMode mounts effects twice
  // in development, and without it the user would get two drafts for one click.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    create
      .mutateAsync(invoiceId)
      .then((created) => {
        toast({ title: `${label} ${created.docNumber} created as draft`, tone: 'success' });
        window.location.hash = `${BASE[docType]}/${created.id}/edit`;
      })
      .catch((cause: unknown) => {
        const message = cause instanceof Error ? cause.message : 'Try again.';
        setError(message);
        toast({ title: `Could not create the ${label.toLowerCase()}`, description: message, tone: 'danger' });
      });
  }, [create, invoiceId, label, docType]);

  return (
    <div className="p-4 text-slate-100">
      {error ? (
        <>
          <p role="alert" className="text-sm text-red-300">
            {error}
          </p>
          <a className="mt-3 inline-block text-sm text-brand-400 underline" href="#/sales/invoices">
            Back to invoices
          </a>
        </>
      ) : (
        <p className="text-sm text-slate-400">Creating a {label.toLowerCase()} draft…</p>
      )}
    </div>
  );
}